import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { setActiveMode } from "store/reducers/inputReducer";
import { selectRememberedSwitch, setRememberedSwitch } from "store/reducers/uiReducer.js";
import { selectActiveModelType } from "store/reducers/selectors/modelSelectors.js";
import { handleExitFullScreen } from 'functions/utilities/exit_full_screen.js'

import BaseWarningDialog from "./BaseWarningDialog.jsx";

const modelPages = require("store/data/model-pages.json");

const ExitExampleModeDialog = (props) => {
  const modelType = useSelector(selectActiveModelType);
  const isIgnored = useSelector(selectRememberedSwitch("ignoreExitExampleModeWarning"));
  const dispatch = useDispatch();
  const { onClose } = props;
  const linkRef = React.useRef(null);
  // Every New-row and Clear button mounts its own copy of this dialog (kept
  // mounted, closed), so fixed ids repeat once per button and aria-labelledby
  // could resolve to another copy's title. One id per instance.
  const instanceId = React.useId();
  const titleId = `exit-example-mode-dialog-title-${instanceId}`;
  const descriptionId = `exit-example-mode-dialog-description-${instanceId}`;

  const [dontShowAgain, setDontShowAgain] = React.useState(isIgnored);

  React.useEffect(() => {
    setDontShowAgain(isIgnored);
  }, [isIgnored]);

  const prevOpenRef = React.useRef(false);

  const persistDontShowAgain = React.useCallback(() => {
    dispatch(
      setRememberedSwitch({ key: "ignoreExitExampleModeWarning", value: Boolean(dontShowAgain) })
    );
  }, [dispatch, dontShowAgain]);

  const handleCancel = () => {
    persistDontShowAgain();
    onClose(false);
  };

  const onContinue = React.useCallback(() => {
    persistDontShowAgain();
    dispatch(setActiveMode({ activeMode: "modeling" }));
    if (linkRef.current) {
      linkRef.current.click();
    }
    onClose(true);
  }, [persistDontShowAgain, dispatch, onClose]);

  const handleToggleDontShowAgain = (event) => {
    const checked = Boolean(event && event.target && event.target.checked);
    setDontShowAgain(checked);
  };

  props.open && handleExitFullScreen();

  React.useEffect(() => {
    const isOpen = Boolean(props.open);
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;

    if (!isOpen) {
      return;
    }

    // Only auto-continue when opening the dialog and the user already chose to ignore it.
    if (!wasOpen && isIgnored) {
      onContinue();
    }
  }, [props.open, isIgnored, onContinue]);

  const isOpeningAndIgnored = Boolean(props.open) && isIgnored && !prevOpenRef.current;

  return (
    <>
      <BaseWarningDialog
        open={Boolean(props.open) && !isOpeningAndIgnored}
        onClose={handleCancel}
        onConfirm={onContinue}
        title="Warning"
        message="You must leave example mode to add or edit data. Press Continue to confirm, or Cancel to stay in example mode."
        confirmText="Continue"
        cancelText="Cancel"
        showDontShowAgain
        dontShowAgain={dontShowAgain}
        onToggleDontShowAgain={handleToggleDontShowAgain}
        titleId={titleId}
        descriptionId={descriptionId}
      />

      <Link hidden={true} ref={linkRef} to={modelPages[modelType]} />
    </>
  );
};

ExitExampleModeDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default ExitExampleModeDialog;
